import { Fragment, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Dialog as UIDialog, Transition } from '@headlessui/react';
import { DeleteSquare } from '@/components/Icons.tsx';
import { Spinner } from '@/components/Loader.tsx';
import MissingTests from '@/components/MissingTests.tsx';
import { TypoCaption, TypoHeading, TypoHeading2 } from '@/components/Typography.tsx';
import { getAvatarBackground, getAvatarUrl } from '@/utils/avatars.ts';

import { PublicPlayer, Tone, TONE_TEXT } from './common.ts';

export const Badge = ({ tone = 'neutral', className = '', children }: {
  tone?: Tone;
  className?: string;
  children: ReactNode;
}) => (
  <TypoCaption className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-xs bg-shade-10 font-semibold
                           whitespace-nowrap ${TONE_TEXT[tone]} ${className}`}>
    {children}
  </TypoCaption>
);

export const Panel = ({ title, action, className = '', children }: {
  title?: ReactNode;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}) => (
  <section className={`bg-neutral2 text-neutral2-text-soft rounded p-6 m:p-4 ${className}`}>
    {(title || action) && (
      <div className="flex items-center justify-between gap-4 mb-4">
        {title && <TypoHeading className="text-neutral2-text-strong font-medium">{title}</TypoHeading>}
        {action}
      </div>
    )}
    {children}
  </section>
);

export const PanelLink = ({ to, children }: { to: string; children: ReactNode }) => (
  <Link to={to} className="text-callout font-semibold text-primary hover:underline whitespace-nowrap">
    {children} →
  </Link>
);

type AvatarPlayer = Pick<PublicPlayer, 'ladderName' | 'avatarIdx' | 'avatarBackgroundIdx'>;

export const PlayerAvatar = ({ player, size = 'md' }: { player?: AvatarPlayer | null; size?: 'sm' | 'md' | 'lg' }) => {
  const sizes = { sm: 'w-7 h-7 text-caption', md: 'w-10 h-10 text-callout', lg: 'w-16 h-16 text-heading' };
  const background = getAvatarBackground(player?.avatarBackgroundIdx);
  const url = getAvatarUrl(player?.avatarIdx);

  return (
    <span
      className={`${sizes[size]} shrink-0 rounded-full flex items-center justify-center overflow-hidden
                  font-bold text-common-black border-2 border-shade-50 uppercase`}
      style={{ background }}
      aria-hidden
    >
      {/* Initials only for an invalid index, e.g. a player who skipped the app's onboarding. */}
      {url
        ? <img src={url} alt="" className="w-full h-full" loading="lazy" />
        : (player?.ladderName ?? '?').trim().slice(0, 2)}
    </span>
  );
};

const BAR_COLORS: Record<string, string> = { true: 'bg-success', false: 'bg-danger' };

export const RoundBars = ({ results, total = 0 }: { results: (boolean | null | undefined)[]; total?: number }) => {
  const bars = [...results, ...Array(Math.max(0, total - results.length)).fill(undefined)];

  return (
    <div className="flex gap-1">
      {bars.map((result, idx) => (
        <span
          key={idx}
          className={`w-4 h-2 rounded-xxs ${BAR_COLORS[String(result)] ?? 'bg-shade-20'}`}
        />
      ))}
    </div>
  );
};

export const ReviewLink = ({ to, children = 'Review' }: { to: string; children?: ReactNode }) => {
  const { pathname } = useLocation();

  return (
    <Link
      to={to}
      state={{ from: pathname }}
      className="inline-flex items-center px-3 py-1.5 rounded-sm bg-info text-info-text hover:bg-info-hover
                 text-footnote font-semibold whitespace-nowrap"
    >
      {children}
    </Link>
  );
};

export const InlineLoader = () => (
  <div className="flex justify-center py-10 text-neutral2-text-softer">
    <Spinner className="w-8" />
  </div>
);

export const EmptyState = ({ text }: { text: string }) => (
  <div className="flex justify-center"><MissingTests text={text} /></div>
);

export const AppNote = ({ className = '' }: { className?: string }) => (
  <TypoCaption className={`text-neutral2-text-softer ${className}`}>
    Matches are played in the SATPrac Ladder app, where every round runs on a live timer.
  </TypoCaption>
);

/** Shared table look; rows stay scrollable sideways when the column is narrow. */
export const Table = ({ head, children }: { head: { label: string; className?: string }[]; children: ReactNode }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-left text-callout">
      <thead>
        <tr className="border-b border-shade-10 text-footnote font-semibold uppercase text-neutral2-text-strong">
          {head.map(({ label, className = '' }) => (
            <th key={label} className={`px-3 py-3 whitespace-nowrap ${className}`}>{label}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-shade-10">{children}</tbody>
    </table>
  </div>
);

export const Modal = ({ open, title, onClose, wide = false, fit = false, children }: {
  open: boolean;
  title: string;
  onClose: () => void;
  wide?: boolean;
  /** Shrink the panel to its content (never narrower than a regular modal) instead of filling the max width. */
  fit?: boolean;
  children: ReactNode;
}) => (
  <Transition show={open} as={Fragment}>
    <UIDialog className="relative z-50" onClose={onClose}>
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-common-black/[.55]" aria-hidden="true" />
      </Transition.Child>

      <Transition.Child
        as="div"
        className="fixed inset-0 flex items-center justify-center p-4"
        enter="ease-out duration-300"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <UIDialog.Panel
          className={`${fit ? 'w-fit min-w-[min(28rem,100%)]' : 'w-full'} ${wide ? 'max-w-[60rem]' : 'max-w-[28rem]'}
                      max-h-[90dvh] flex flex-col
                      bg-neutral rounded-md text-neutral-text`}
        >
          <div className="flex items-center justify-between gap-4 px-6 pt-5 pb-3 m:px-4">
            <UIDialog.Title as={TypoHeading2} className="font-medium text-neutral-text-strong m:text-heading2-m">
              {title}
            </UIDialog.Title>
            <button
              onClick={onClose}
              aria-label="Close"
              className="text-neutral-canvas hover:text-neutral-canvas-hover"
            >
              <DeleteSquare />
            </button>
          </div>
          <div className="min-h-0 overflow-y-auto px-6 pb-6 m:px-4">{children}</div>
        </UIDialog.Panel>
      </Transition.Child>
    </UIDialog>
  </Transition>
);
